import React, { FC, useContext, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { Box, Button } from '@mui/material'
import Grid from '@mui/material/Grid'
import { errorMapper } from 'src/i18n/ErrorMapper'
import { useTranslate } from 'src/i18n/useMessageSource'
import { Configuration, LaundryGroup, LaundryGroupsApi } from 'src/service/backend/api'
import { HttpContext } from 'src/service/backend/http/HttpContext'
import {
  LaundryGroupViewModel,
  createLaundryGroupViewModel,
  mapViewModelToLaundryGroup,
} from 'src/service/view-model/laundry-group/LaundryGroupViewModel'
import { ErrorMessage } from 'src/ui-shared/base/error-message/ErrorMessage'
import { DetailsTextField } from 'src/ui-shared/base/form/control/DetailsTextField'
import { TextField } from 'src/ui-shared/base/form/control/TextFieldValidate'
import { composeValidators, required, validAmount, validAmountMax } from 'src/ui-shared/base/form/validation/Validators'
import { LoadingIndicator } from 'src/ui-shared/base/loading-indicator/LoadingIndicator'
import { useShowSnackbar } from 'src/ui-shared/base/snackbar/SnackbarProvider'

interface Props {
  laundryGroup: LaundryGroup
  closeModal: (successMessage?: string) => void
}

export const LaundryGroupEnergyConfig: FC<Props> = ({ laundryGroup, closeModal }) => {
  const translate = useTranslate()
  const showSnackbar = useShowSnackbar()

  const httpConfiguration: Configuration = useContext(HttpContext)
  const laundryGroupsApi = new LaundryGroupsApi(httpConfiguration)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [viewModel, setViewModel] = useState<LaundryGroupViewModel>(createLaundryGroupViewModel())

  const priceWaterMaxAmount = 2000
  const priceElectricityMaxAmount = 2000

  // load data
  useEffect(() => {
    let active = true
    setLoading(true)

    laundryGroupsApi
      .laundrygroupsLaundryGroupIdGet(laundryGroup.id!, [])
      .then((laundryGroup) => {
        if (active) {
          const laundryGroupViewModel = createLaundryGroupViewModel(laundryGroup)
          setViewModel(laundryGroupViewModel)
          setLoading(false)
          setErrorMessage(null)
        }
      })
      .catch((err) => {
        const errorMessage = errorMapper(err, translate)
        console.error(errorMessage, err)
        setErrorMessage(errorMessage)
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const updateLaundryGroup = (newLaundryGroup: LaundryGroup) => {
    setLoading(true)
    laundryGroupsApi
      .laundrygroupsLaundryGroupIdPut(laundryGroup.id, [], newLaundryGroup)
      .then((_data) => {
        closeModal(translate('energyPricesSaved'))
        setLoading(false)
      })
      .catch((err) => {
        const errorMessage = errorMapper(err, translate)
        console.error(errorMessage, err)
        showSnackbar(errorMessage, 'error')
        setLoading(false)
      })
  }

  const submitForm = (viewModel: LaundryGroupViewModel) => {
    const laundryGroup = mapViewModelToLaundryGroup(viewModel)
    updateLaundryGroup(laundryGroup)
  }

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <LoadingIndicator loading={loading} />
      {errorMessage ? <ErrorMessage message={errorMessage} /> : null}
      <Form<LaundryGroupViewModel>
        initialValues={viewModel}
        onSubmit={submitForm}
        render={({ handleSubmit, submitting, pristine }) => {
          return (
            <form onSubmit={handleSubmit} autoComplete="off">
              <Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="servicemaster.priceWater"
                      label={translate('priceWater')}
                      disabled={loading}
                      validate={composeValidators(
                        required(),
                        validAmount('validation.valid.amount'),
                        validAmountMax(undefined, priceWaterMaxAmount),
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="servicemaster.priceElectricity"
                      label={translate('priceElectricity')}
                      disabled={loading}
                      validate={composeValidators(
                        required(),
                        validAmount('validation.valid.amount'),
                        validAmountMax(undefined, priceElectricityMaxAmount),
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailsTextField value={laundryGroup.currency} label={translate('currency')} />
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="text"
                  color="primary"
                  size="large"
                  sx={{ marginRight: 2 }}
                  onClick={() => closeModal()}
                >
                  {translate('button.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={submitting || pristine || loading}
                >
                  {translate('apply')}
                </Button>
              </Box>
            </form>
          )
        }}
      />
    </Box>
  )
}
